import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMeta } from "@/lib/api";

export const emptyShipment = () => ({
  sender: { name: "", email: "", phone: "", address: "" },
  receiver: { name: "", email: "", phone: "", address: "" },
  origin: "", destination: "",
  package: { contents: "", weight_kg: "", dimensions: "", quantity: 1, declared_value: 0, service: "Express Courier" },
  notes: "",
});

export const toPayload = (f) => ({
  ...f,
  sender: { ...f.sender, email: f.sender.email || null },
  receiver: { ...f.receiver, email: f.receiver.email || null },
  package: { ...f.package, weight_kg: Number(f.package.weight_kg), quantity: Number(f.package.quantity) || 1, declared_value: Number(f.package.declared_value) || 0 },
});

const Field = ({ label, id, children }) => (
  <div className="space-y-1.5"><Label htmlFor={id} className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>{children}</div>
);

function PartyFields({ title, prefix, value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="space-y-4">
      <h3 className="font-display font-semibold text-[#0A192F]">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name" id={`${prefix}-name`}><Input id={`${prefix}-name`} required value={value.name} onChange={set("name")} data-testid={`${prefix}-name-input`} /></Field>
        <Field label="Email" id={`${prefix}-email`}><Input id={`${prefix}-email`} type="email" value={value.email || ""} onChange={set("email")} data-testid={`${prefix}-email-input`} /></Field>
        <Field label="Phone" id={`${prefix}-phone`}><Input id={`${prefix}-phone`} value={value.phone} onChange={set("phone")} data-testid={`${prefix}-phone-input`} /></Field>
        <Field label="Address" id={`${prefix}-address`}><Input id={`${prefix}-address`} required value={value.address} onChange={set("address")} data-testid={`${prefix}-address-input`} /></Field>
      </div>
    </div>
  );
}

export function ShipmentFields({ form, setForm }) {
  const { data: meta } = useMeta();
  const setPkg = (k, v) => setForm({ ...form, package: { ...form.package, [k]: v } });
  return (
    <div className="space-y-8">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Origin city, country" id="origin"><Input id="origin" required placeholder="e.g. Houston, USA" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="origin-input" /></Field>
        <Field label="Destination city, country" id="destination"><Input id="destination" required placeholder="e.g. Accra, Ghana" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="destination-input" /></Field>
      </div>
      <PartyFields title="Sender" prefix="sender" value={form.sender} onChange={(v) => setForm({ ...form, sender: v })} />
      <PartyFields title="Receiver" prefix="receiver" value={form.receiver} onChange={(v) => setForm({ ...form, receiver: v })} />
      <div className="space-y-4">
        <h3 className="font-display font-semibold text-[#0A192F]">Package</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Contents" id="contents"><Input id="contents" required value={form.package.contents} onChange={(e) => setPkg("contents", e.target.value)} data-testid="package-contents-input" /></Field>
          <Field label="Weight (kg)" id="weight"><Input id="weight" required type="number" min="0.1" step="0.1" value={form.package.weight_kg} onChange={(e) => setPkg("weight_kg", e.target.value)} data-testid="package-weight-input" /></Field>
          <Field label="Quantity" id="qty"><Input id="qty" type="number" min="1" value={form.package.quantity} onChange={(e) => setPkg("quantity", e.target.value)} data-testid="package-quantity-input" /></Field>
          <Field label="Dimensions" id="dims"><Input id="dims" placeholder="L x W x H cm" value={form.package.dimensions} onChange={(e) => setPkg("dimensions", e.target.value)} data-testid="package-dimensions-input" /></Field>
          <Field label="Declared value (USD)" id="value"><Input id="value" type="number" min="0" value={form.package.declared_value} onChange={(e) => setPkg("declared_value", e.target.value)} data-testid="package-value-input" /></Field>
          <Field label="Shipping method" id="service">
            <Select value={form.package.service} onValueChange={(v) => setPkg("service", v)}>
              <SelectTrigger id="service" data-testid="package-service-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(meta?.services || { "Express Courier": 8 }).map((s) => <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </div>
      <Field label="Notes (optional)" id="notes"><Textarea id="notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="notes-input" /></Field>
    </div>
  );
}
